import { ReactElement } from "react";
import { ComponentProps } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import i18n from "src/i18n";

function getOptionsByVariables(type: string, data: string[]) {
  return data.map((item) => ({
    label: `${type}.${item}`,
    value: `{{${type}.${item}}}`,
  }));
}

export function createAvailableVariables(
  t: (key: string, fallback?: string) => string,
  contactVars: string[],
  agentVariables: string[],
  UserIcon: (props: ComponentProps<"svg">) => ReactElement
) {
  const agentOptions = getOptionsByVariables("user", agentVariables);
  const contactOptions = getOptionsByVariables("contact", contactVars);

  return [
    {
      label: t("inbox:quick-replies.variables.user", "User Variables"),
      icon: UserIcon,
      options: agentOptions,
    },
    {
      label: t("inbox:quick-replies.variables.contact", "Contact Variables"),
      icon: UserIcon,
      options: contactOptions,
    },
  ];
}

export function formatOptionLabel({
  value,
  label,
  icon,
  help,
  t,
}: {
  value: string;
  label: string;
  icon: ReactElement;
  help?: string;
  t: (key: string) => string;
  [key: string]: any;
}) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex items-center">
        <div className="mx-4">{icon}</div>
        <div>
          <h4 className="text-sm">{t(label)}</h4>
          {help && <p className="text-xs">{t(help)}</p>}
        </div>
      </div>
    </div>
  );
}

export function setupInitialFormState(
  formValues: {
    body_type: string;
    headers: { key: string; value: string }[];
  },
  formik: any,
  formPrefix: string
) {
  let needsUpdate = false;
  let updates: Record<string, any> = {};

  if (!formValues.body_type) {
    updates[`${formPrefix}.body_type`] = "application/json";
    needsUpdate = true;
  } else if (formValues.body_type === "JSON") {
    updates[`${formPrefix}.body_type`] = "application/json";
    needsUpdate = true;
  }

  if (
    formValues.body_type &&
    formValues.body_type !== "NONE" &&
    formValues.headers &&
    !formValues.headers.some((h) => h.key.toLowerCase() === "content-type")
  ) {
    const updatedHeaders = [
      ...formValues.headers,
      {
        key: "Content-Type",
        value: formValues.body_type,
      },
    ];
    updates[`${formPrefix}.headers`] = updatedHeaders;
    needsUpdate = true;
  }

  if (needsUpdate) {
    Object.entries(updates).forEach(([key, value]) => {
      formik.setFieldValue(key, value);
    });
  }

  return needsUpdate;
}

/**
 * Determines the appropriate syntax highlighting language based on content type
 */
export function getCodeLanguage(contentType: string): string {
  if (typeof contentType === "string") {
    if (contentType.includes("yaml")) {
      return "yaml";
    } else if (contentType.includes("xml")) {
      return "markup";
    } else if (contentType.includes("json")) {
      return "json";
    }
  }
  return "javascript";
}

/**
 * Highlights code using the SyntaxHighlighter component
 */
export function highlightCode(
  code: string,
  contentType: string
): ReactElement | string {
  try {
    return (
      <SyntaxHighlighter
        language={getCodeLanguage(contentType)}
        style={oneLight}
        customStyle={{
          margin: 0,
          padding: 0,
          background: "transparent",
        }}
        codeTagProps={{
          style: {
            fontSize: "inherit",
            fontFamily: "inherit",
          },
        }}
      >
        {code}
      </SyntaxHighlighter>
    );
  } catch (e) {
    console.error("Error highlighting code:", e);
    return code;
  }
}

/**
 * Formats response body for display, handling JSON parsing and formatting
 */
export function formatResponseBody(body: any): string {
  if (!body)
    return i18n.t("inbox-actions:builder.test.no-response", "No response");

  try {
    if (typeof body === "object") {
      return JSON.stringify(body, null, 2);
    }

    if (typeof body === "string") {
      if (body.trim().startsWith("{") || body.trim().startsWith("[")) {
        try {
          const parsed = JSON.parse(body);
          return JSON.stringify(parsed, null, 2);
        } catch (e) {
          return body;
        }
      }
      return body;
    }

    return String(body);
  } catch (e) {
    return String(body);
  }
}

/**
 * Checks if a body is valid JSON (for highlighting decisions)
 */
export function isBodyJson(body: any): boolean {
  if (!body) return false;

  if (typeof body === "object") return true;

  if (typeof body === "string") {
    try {
      const trimmed = body.trim();
      if (
        (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
        (trimmed.startsWith("[") && trimmed.endsWith("]"))
      ) {
        JSON.parse(trimmed);
        return true;
      }
    } catch (e) {
      return false;
    }
  }

  return false;
}
